import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, downloadCsv } from "@/lib/api";
import { ENQUIRY_OPTIONS, PRODUCTS } from "@/data/products";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Trash2,
  Mail,
  Phone,
  Loader2,
  Plus,
  X,
  Download,
  Package,
  BarChart3,
} from "lucide-react";

const ORDER_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "in_production", label: "In Production" },
  { value: "shipped", label: "Shipped" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_STYLES = {
  pending: "bg-white/15 text-white/70",
  paid: "bg-emerald-500/90 text-black",
  in_production: "bg-amber-500/90 text-black",
  shipped: "bg-sky-500/90 text-black",
  completed: "bg-[#e31e24] text-white",
  cancelled: "bg-white/10 text-white/40 line-through",
};

const REVENUE_STATUSES = ["paid", "in_production", "shipped", "completed"];

const field =
  "w-full bg-[#0e0e0e] border border-white/12 px-4 py-3 text-white placeholder:text-white/30 text-sm outline-none focus:border-[#e31e24] transition-colors";

const money = (n) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
  }).format(n || 0);

const emptyForm = {
  customer_name: "",
  email: "",
  phone: "",
  part: "",
  amount: "",
  status: "paid",
  notes: "",
};

export const AdminOrders = ({ prefill, onPrefillConsumed }) => {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .listOrders()
      .then((res) => setItems(res.data))
      .catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
  }, []);

  // Prefill the new-order form when converting from an enquiry
  useEffect(() => {
    if (!prefill) return;
    const product = PRODUCTS.find((p) => p.name === prefill.part);
    const amount =
      product && !product.priceOnEnquiry ? String(product.price.amount) : "";
    setForm({
      customer_name: prefill.name || "",
      email: prefill.email || "",
      phone: prefill.phone || "",
      part: ENQUIRY_OPTIONS.includes(prefill.part) ? prefill.part : "",
      amount,
      status: "pending",
      notes: prefill.message ? `From enquiry: ${prefill.message}` : "",
    });
    setShowForm(true);
    toast.info("Enquiry loaded — review the amount, then create the order.");
    onPrefillConsumed && onPrefillConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const monthly = useMemo(() => {
    const list = items || [];
    const now = new Date();
    const keys = [];
    const map = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      keys.push(k);
      map[k] = {
        month: d.toLocaleString("en-AU", { month: "short" }),
        revenue: 0,
      };
    }
    list
      .filter((o) => REVENUE_STATUSES.includes(o.status))
      .forEach((o) => {
        const d = new Date(o.created_at);
        const k = `${d.getFullYear()}-${d.getMonth()}`;
        if (map[k]) map[k].revenue += o.amount || 0;
      });
    return keys.map((k) => map[k]);
  }, [items]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email) {
      toast.error("Customer name and email are required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.createOrder({
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      setItems((cur) => [data, ...cur]);
      setForm(emptyForm);
      setShowForm(false);
      toast.success("Order created.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.updateOrder(id, { status });
      setItems((cur) => cur.map((o) => (o.id === id ? data : o)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteOrder(id);
      setItems((cur) => cur.filter((o) => o.id !== id));
      toast.success("Order deleted.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportCsv = () => {
    if (!items || items.length === 0) {
      toast.error("No orders to export.");
      return;
    }
    downloadCsv(
      `rayce-parts-orders-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "created_at", label: "Date" },
        { key: "customer_name", label: "Customer" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "part", label: "Part" },
        { key: "amount", label: "Amount (AUD)" },
        { key: "status", label: "Status" },
        { key: "notes", label: "Notes" },
      ],
      items
    );
    toast.success(`Exported ${items.length} orders.`);
  };

  if (items === null)
    return (
      <div className="grid place-items-center py-24 text-white/40">
        <Loader2 className="animate-spin" />
      </div>
    );

  const revenue = items
    .filter((o) => REVENUE_STATUSES.includes(o.status))
    .reduce((a, o) => a + (o.amount || 0), 0);

  return (
    <div data-testid="admin-orders">
      {/* summary + actions */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="bg-[#141414] border border-white/10 px-5 py-4">
          <span className="font-mono-rp text-[10px] tracking-[0.2em] uppercase text-white/40 block">
            Revenue (paid+)
          </span>
          <span className="font-display text-3xl text-[#e31e24]">
            {money(revenue)}
          </span>
        </div>
        <div className="bg-[#141414] border border-white/10 px-5 py-4">
          <span className="font-mono-rp text-[10px] tracking-[0.2em] uppercase text-white/40 block">
            Total Orders
          </span>
          <span className="font-display text-3xl text-white">
            {items.length}
          </span>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={exportCsv}
            data-testid="orders-export-csv"
            className="flex items-center gap-2 font-mono-rp text-[11px] tracking-[0.15em] uppercase px-3 py-2.5 border border-white/15 text-white/60 hover:text-white hover:border-[#e31e24] transition-colors"
          >
            <Download size={13} /> Export CSV
          </button>
          <button
            onClick={() => setShowForm((s) => !s)}
            data-testid="orders-add-toggle"
            className="btn-race !py-2.5 !px-4 text-sm"
          >
            {showForm ? <X size={15} /> : <Plus size={15} />}
            {showForm ? "Cancel" : "New Order"}
          </button>
        </div>
      </div>

      {/* monthly revenue chart */}
      {items.length > 0 && (
        <div
          className="bg-[#141414] border border-white/10 p-5 md:p-6 mb-8"
          data-testid="orders-revenue-chart"
        >
          <div className="flex items-center gap-2 mb-5">
            <BarChart3 size={16} className="text-[#e31e24]" />
            <span className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/60">
              Revenue · last 6 months
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="month"
                tick={{ fill: "#9a9a9a", fontSize: 12, fontFamily: "IBM Plex Mono" }}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                tickLine={false}
              />
              <YAxis
                domain={[0, (max) => Math.max(100, Math.ceil((max * 1.25) / 50) * 50)]}
                tick={{ fill: "#9a9a9a", fontSize: 12, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${v / 1000}k` : `$${v}`)}
              />
              <Tooltip
                cursor={{ fill: "rgba(227,30,36,0.08)" }}
                contentStyle={{
                  background: "#0e0e0e",
                  border: "1px solid rgba(227,30,36,0.4)",
                  borderRadius: 0,
                  color: "#fff",
                  fontFamily: "IBM Plex Mono",
                  fontSize: 12,
                }}
                formatter={(v) => [money(v), "Revenue"]}
              />
              <Bar dataKey="revenue" fill="#e31e24" radius={[2, 2, 0, 0]} maxBarSize={54} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* add form */}
      {showForm && (
        <form
          onSubmit={create}
          noValidate
          className="bg-[#141414] border border-white/10 p-6 mb-8"
          data-testid="order-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              className={field}
              placeholder="Customer name *"
              value={form.customer_name}
              onChange={upd("customer_name")}
              data-testid="order-customer"
            />
            <input
              type="email"
              className={field}
              placeholder="Email *"
              value={form.email}
              onChange={upd("email")}
              data-testid="order-email"
            />
            <input
              className={field}
              placeholder="Phone (optional)"
              value={form.phone}
              onChange={upd("phone")}
              data-testid="order-phone"
            />
            <Select
              value={form.part}
              onValueChange={(v) => setForm((f) => ({ ...f, part: v }))}
            >
              <SelectTrigger
                className="w-full bg-[#0e0e0e] border-white/12 rounded-none py-3 h-auto text-white focus:ring-0"
                data-testid="order-part"
              >
                <SelectValue placeholder="Part" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/15 text-white rounded-none">
                {ENQUIRY_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} className="rounded-none">
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="number"
              min="0"
              step="0.01"
              className={field}
              placeholder="Amount (AUD)"
              value={form.amount}
              onChange={upd("amount")}
              data-testid="order-amount"
            />
            <Select
              value={form.status}
              onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
            >
              <SelectTrigger
                className="w-full bg-[#0e0e0e] border-white/12 rounded-none py-3 h-auto text-white focus:ring-0"
                data-testid="order-status"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/15 text-white rounded-none">
                {ORDER_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value} className="rounded-none">
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <textarea
            rows={2}
            className={field + " resize-none mt-4"}
            placeholder="Notes (build spec, colour, shipping…)"
            value={form.notes}
            onChange={upd("notes")}
            data-testid="order-notes"
          />
          <button
            type="submit"
            disabled={saving}
            className="btn-race mt-4 justify-center disabled:opacity-60"
            data-testid="order-save"
          >
            {saving ? "Saving…" : "Create Order"}
          </button>
        </form>
      )}

      {/* list */}
      {items.length === 0 ? (
        <div className="grid place-items-center py-24 text-white/40 border border-white/10 border-dashed">
          <Package size={28} />
          <p className="mt-3 text-sm">
            No orders yet. Add a paid build with "New Order".
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((o) => (
            <div
              key={o.id}
              className="bg-[#141414] border border-white/10 p-5 md:p-6"
              data-testid={`order-row-${o.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-2xl text-white">
                      {o.customer_name}
                    </h3>
                    <span
                      className={`text-[10px] font-mono-rp uppercase tracking-widest px-2 py-1 ${
                        STATUS_STYLES[o.status]
                      }`}
                      data-testid={`order-status-badge-${o.id}`}
                    >
                      {o.status.replace("_", " ")}
                    </span>
                    <span className="font-display text-xl text-[#e31e24]">
                      {money(o.amount)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-white/60">
                    <a
                      href={`mailto:${o.email}`}
                      className="flex items-center gap-1.5 hover:text-white"
                    >
                      <Mail size={13} /> {o.email}
                    </a>
                    {o.phone && (
                      <span className="flex items-center gap-1.5">
                        <Phone size={13} /> {o.phone}
                      </span>
                    )}
                    {o.part && (
                      <span className="text-[#e31e24]/90">{o.part}</span>
                    )}
                    <span className="text-white/35">
                      {new Date(o.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={o.status}
                    onValueChange={(v) => setStatus(o.id, v)}
                  >
                    <SelectTrigger
                      className="w-[160px] bg-[#0e0e0e] border-white/12 rounded-none h-10 text-white text-sm focus:ring-0"
                      data-testid={`order-status-select-${o.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-white/15 text-white rounded-none">
                      {ORDER_STATUSES.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          className="rounded-none"
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => remove(o.id)}
                    className="grid place-items-center h-10 w-10 border border-white/12 text-white/50 hover:text-[#e31e24] hover:border-[#e31e24] transition-colors"
                    data-testid={`order-delete-${o.id}`}
                    aria-label="Delete order"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {o.notes && (
                <p className="mt-4 text-white/70 text-sm bg-[#0e0e0e] border border-white/8 p-4 whitespace-pre-wrap">
                  {o.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
